import * as React from 'react'
import { useCallback, useContext, useEffect } from 'react'
import { TabsContext } from './Tabs'
import styled, { css } from 'styled-components'

const createStyleForTheme = (classSuffix: string, colorObject: { [key: number]: string }) => {
  return css`
    .theme-${classSuffix} & {
      &:not(.is-active) {
        &:hover,
        &:focus {
          --bg-color: ${colorObject[2]};
          --text-color: ${({ theme }) => theme.colors.white[1]};
        }

        &:active,
        &:focus-within {
          --bg-color: ${colorObject[5]};
          --text-color: ${({ theme }) => theme.colors.black[1]};
        }
      }

      &.is-active {
        --bg-color: ${colorObject[5]};
        --text-color: ${({ theme }) => theme.colors.black[1]};
      }
    }
  `
}

const StyledTabNavigationItem = styled.button`
  --bg-color: transparent;
  --text-color: ${({ theme }) => theme.colors.black[3]};

  background-color: var(--bg-color);
  border-radius: 0.6rem;
  border: none;
  color: var(--text-color);
  cursor: pointer;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1;
  outline: 0 solid var(--bg-color);
  padding: 0.5rem 0.8rem;
  transition: all 0.1s ease-out;
  user-select: none;
  white-space: nowrap;

  &:not(.is-active) {
    &:hover,
    &:focus {
      --bg-color: ${({ theme }) => theme.colors.azure[2]};
      --text-color: ${({ theme }) => theme.colors.white[1]};

      outline-width: 0.15rem;
      position: relative;
      z-index: 1;
    }

    &:active {
      --bg-color: ${({ theme }) => theme.colors.azure[5]};
      --text-color: ${({ theme }) => theme.colors.black[1]};
    }
  }

  &.is-active,
  &.is-active:focus {
    --bg-color: ${({ theme }) => theme.colors.azure[5]};
    --text-color: ${({ theme }) => theme.colors.black[1]};
    cursor: default;
  }

  ${({ theme }) => createStyleForTheme('electric-indigo', theme.colors.electricIndigo)}
  ${({ theme }) => createStyleForTheme('limegreen', theme.colors.limegreen)}
  ${({ theme }) => createStyleForTheme('bluetiful', theme.colors.bluetiful)}
  ${({ theme }) => createStyleForTheme('red', theme.colors.red)}
  ${({ theme }) => createStyleForTheme('orange', theme.colors.orange)}
  ${({ theme }) => createStyleForTheme('yellow', theme.colors.yellow)}
  ${({ theme }) => createStyleForTheme('turquoise', theme.colors.turquoise)}
  ${({ theme }) => createStyleForTheme('fuchsia', theme.colors.fuchsia)}
  ${({ theme }) => createStyleForTheme('magenta', theme.colors.magenta)}
`

export interface TabNavigationItemProps {
  children: React.ReactNode
  tabId: string
}

export const TabNavigationItem = ({ children, tabId }: TabNavigationItemProps) => {
  const { activeTabId, setActive, registerTab, unregisterTab } = useContext(TabsContext)

  // memorize onClick function so no new functions are created on each render
  const handleClick = useCallback(() => {
    setActive(tabId)
  }, [tabId])

  useEffect(() => {
    registerTab(tabId)

    return () => {
      unregisterTab(tabId)
    }
  }, [tabId, registerTab, unregisterTab])

  const isActive = tabId === activeTabId

  return (
    <StyledTabNavigationItem onClick={handleClick} className={isActive ? 'is-active' : ''}>
      {children}
    </StyledTabNavigationItem>
  )
}
